import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Textarea,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Spinner,
  Checkbox,
  InputGroup,
  InputRightElement,
  TableContainer,
  useBreakpointValue,
} from "@chakra-ui/react";
import axios from "axios";
import { getHeadersForRequest } from "../utils/functions";

interface ProductRow {
  product: string;
  producedQty?: string;
  soldQty?: string;
  stockAvailable?: string;
  openingBalance?: string;
  currentStock: number;
  physicalQty: number | "";
  isSame: boolean;
  include: boolean;
}

interface ManualStockTakeFormProps {
  inventoryData: any;
}

const ManualStockTakeForm: React.FC<ManualStockTakeFormProps> = ({
  inventoryData,
}) => {
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [auditDate, setAuditDate] = useState<string>("");
  const [isStartDay, setIsStartDay] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const toast = useToast();

  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (inventoryData.length > 0) {
      const formatted = inventoryData.map((item: any) => ({
        product: item.product,
        currentStock: parseFloat(
          item.stockAvailable || item.availableQty || "0"
        ),
        physicalQty: "",
        isSame: false,
        include: false,
      }));
      setProducts(formatted);
      setLoading(false);
    }
  }, [inventoryData]);

  const handleQtyChange = (index: number, value: string) => {
    const updated = [...products];
    updated[index].physicalQty = value === "" ? "" : parseFloat(value);
    updated[index].isSame = false;
    setProducts(updated);
  };

  const toggleIsSame = (index: number) => {
    const updated = [...products];
    updated[index].isSame = !updated[index].isSame;
    updated[index].physicalQty = updated[index].isSame
      ? updated[index].currentStock
      : "";
    setProducts(updated);
  };

  const toggleInclude = (index: number) => {
    const updated = [...products];
    updated[index].include = !updated[index].include;
    setProducts(updated);
  };

  const handleSubmit = async () => {
    const items = products.filter((p) => p.include && p.physicalQty !== "");
    if (!auditDate || items.length === 0) {
      toast({
        title: "Audit date and at least one included item are required",
        status: "warning",
      });
      return;
    }

    try {
      const headers = await getHeadersForRequest();
      const res = await axios.post(
        `${process.env.REACT_APP_TEST_API_URL}/products/manualStockTake`,
        {
          auditDate,
          performedBy: headers?.emailAddress,
          notes,
          isStartDay,
          items,
        },
        { headers }
      );

      toast({
        title: "Stock audit saved",
        description: `Audit ID: ${res.data.auditId}`,
        status: "success",
      });
    } catch (err) {
      console.error("Failed to submit audit:", err);
      toast({ title: "Failed to submit audit", status: "error" });
    }
  };

  if (loading) return <Spinner size="xl" mt={10} />;

  return (
    <Box p={6} maxW="6xl" mx="auto">
      <Heading mb={6}>Manual Stock Take</Heading>

      <VStack align="start" spacing={4} mb={6}>
        <Input
          type="date"
          value={auditDate}
          onChange={(e) => setAuditDate(e.target.value)}
          placeholder="Audit Date"
        />
        <Textarea
          placeholder="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <label>
          <input
            type="checkbox"
            checked={isStartDay}
            onChange={(e) => setIsStartDay(e.target.checked)}
          />{" "}
          Audit was done before production/sales that day (Start of Day)
        </label>
      </VStack>

      <TableContainer>
        <Table size={isSmallScreen ? "sm" : "md"} variant="striped" mb={6}>
          <Thead>
            <Tr>
              <Th isNumeric>Include</Th>
              <Th>Product</Th>
              <Th isNumeric>Current Stock</Th>
              <Th isNumeric>Same ?</Th>
              <Th isNumeric>Physical Qty (KG)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {products.map((item, index) => (
              <Tr key={`${item.product}-${index}`}>
                <Td isNumeric>
                  <Checkbox
                    isChecked={item.include}
                    onChange={() => toggleInclude(index)}
                  />
                </Td>
                <Td>{item.product}</Td>
                <Td isNumeric>{item.currentStock.toFixed(2)}</Td>
                <Td isNumeric>
                  <Checkbox
                    isChecked={item.isSame}
                    onChange={() => toggleIsSame(index)}
                  />
                </Td>
                <Td isNumeric>
                  <InputGroup justifyContent={"flex-end"}>
                    <Input
                      maxW={"150px"}
                      variant={"outline"}
                      type="number"
                      value={item.physicalQty}
                      onChange={(e) => handleQtyChange(index, e.target.value)}
                      step="0.01"
                      min="0"
                    />
                    <InputRightElement
                      pointerEvents="none"
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      KG
                    </InputRightElement>
                  </InputGroup>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Flex justify="end">
        <Button colorScheme="blue" onClick={handleSubmit}>
          Submit Stock Take
        </Button>
      </Flex>
    </Box>
  );
};

export default ManualStockTakeForm;
